export const UPSELLS_TYPES = {
  SALSA: 'SALSA',
  DESSERT: 'DESSERT',
  SIDE: 'SIDE',
  DRINK: 'DRINK',
};

/** Parsed from Remote Config menu JSON: `upsells`, `salsas`, `utensils`. */
export type RemoteUpsellSalsaUtensilsIds = {
  upsells: { sides: number[]; desserts: number[]; drinks: number[] };
  salsas: number[];
  utensils: number[];
};

export function parseUpsellsSalsaUtensilsIds(
  remoteConfigData: unknown,
): RemoteUpsellSalsaUtensilsIds | null {
  if (!remoteConfigData || typeof remoteConfigData !== 'object') return null;
  const d = remoteConfigData as Record<string, unknown>;
  const parseNumArr = (arr: unknown): number[] =>
    Array.isArray(arr)
      ? arr.map((x) => Number(x)).filter((n) => !Number.isNaN(n))
      : [];
  const upsellsRaw = d.upsells;
  const upsellsObj =
    upsellsRaw && typeof upsellsRaw === 'object'
      ? (upsellsRaw as Record<string, unknown>)
      : null;
  const sides = parseNumArr(upsellsObj?.sides);
  const desserts = parseNumArr(upsellsObj?.desserts);
  const drinks = parseNumArr(upsellsObj?.drinks);
  const salsas = parseNumArr(d.salsas);
  const utensils = parseNumArr(d.utensils);

  const hasSomething =
    sides.length ||
    desserts.length ||
    drinks.length ||
    salsas.length ||
    utensils.length;
  if (!hasSomething) return null;
  return {
    upsells: { sides, desserts, drinks },
    salsas,
    utensils,
  };
}

export function findMenuProductById(menu: any, productId: number): any | null {
  for (const cat of menu?.categories ?? []) {
    const p = (cat.products ?? []).find(
      (x: any) => Number(x.id) === Number(productId),
    );
    if (p) {
      return {
        ...p,
        categoryInfo: p.categoryInfo ?? { id: cat.id, name: cat.name },
      };
    }
  }
  return null;
}

/** Remote Config lists `chainproductid` (chain-wide); resolve to the live menu product + ordering `id` for APIs. */
export function findMenuProductByChainProductId(
  menu: any,
  chainProductId: number,
): any | null {
  const want = Number(chainProductId);
  if (Number.isNaN(want)) return null;
  for (const cat of menu?.categories ?? []) {
    const p = (cat.products ?? []).find((x: any) => {
      const chainId = x.chainproductid ?? x.chainProductId;
      return chainId != null && Number(chainId) === want;
    });
    if (p) {
      return {
        ...p,
        categoryInfo: p.categoryInfo ?? { id: cat.id, name: cat.name },
      };
    }
  }
  return null;
}

const isUpsellProduct = (product: any) => {
  const metadata = product?.metadata ?? [];
  return metadata.some(
    (item: any) =>
      item?.key === 'isUpsell' &&
      (item?.value === true || item?.value === 'true'),
  );
};

const isSalsaProduct = (product: any) => {
  const metadata = product?.metadata ?? [];
  return metadata.some(
    (item: any) =>
      item?.key === 'isSalsa' &&
      (item?.value === true || item?.value === 'true'),
  );
};

const normalizeUpsellType = (categoryName: string): string => {
  const name = (categoryName ?? '').toUpperCase();
  if (name.includes('SIDE')) return UPSELLS_TYPES.SIDE;
  if (name.includes('DRINK')) return UPSELLS_TYPES.DRINK;
  if (name.includes('DESSERT')) return UPSELLS_TYPES.DESSERT;
  return categoryName ?? '';
};

function getUpsellProductsFromMenuMetadata(menu: any) {
  const categories = menu?.categories ?? [];
  const upsells = categories
    .map((category: any) => {
      const categoryProducts = category?.products ?? [];
      const products = categoryProducts.filter(isUpsellProduct);
      if (products.length === 0) {
        return null;
      }
      const normalizedType = normalizeUpsellType(category?.name ?? '');
      return {
        type: normalizedType || category?.name,
        categoryid: [category?.id].filter(Boolean),
        products,
      };
    })
    .filter(Boolean) as any[];

  const salsaCategory = categories.find((cat: any) =>
    cat?.name?.toUpperCase?.().includes?.('SALSA'),
  );
  if (salsaCategory) {
    const salsaProducts = (salsaCategory?.products ?? []).filter(isSalsaProduct);
    if (salsaProducts.length > 0) {
      upsells.push({
        type: UPSELLS_TYPES.SALSA,
        categoryid: [salsaCategory?.id].filter(Boolean),
        products: salsaProducts,
      });
    }
  }

  return upsells;
}

function getUpsellProductsFromConfigIds(menu: any, parsed: RemoteUpsellSalsaUtensilsIds) {
  const result: any[] = [];
  const { sides, desserts, drinks } = parsed.upsells;

  const pushGroup = (type: string, ids: number[]) => {
    if (!ids.length) return;
    const products: any[] = [];
    const seenIds = new Set<number>();
    for (const chainPid of ids) {
      const p = findMenuProductByChainProductId(menu, chainPid);
      if (!p?.id) continue;
      const oid = Number(p.id);
      if (seenIds.has(oid)) continue;
      seenIds.add(oid);
      products.push(p);
    }
    if (!products.length) return;
    result.push({
      type,
      categoryid: [products[0]?.categoryInfo?.id].filter(Boolean),
      products,
    });
  };

  pushGroup(UPSELLS_TYPES.SIDE, sides);
  pushGroup(UPSELLS_TYPES.DESSERT, desserts);
  pushGroup(UPSELLS_TYPES.DRINK, drinks);

  if (parsed.salsas.length) {
    const products: any[] = [];
    const seenIds = new Set<number>();
    for (const chainPid of parsed.salsas) {
      const p = findMenuProductByChainProductId(menu, chainPid);
      if (!p?.id) continue;
      const oid = Number(p.id);
      if (seenIds.has(oid)) continue;
      seenIds.add(oid);
      products.push(p);
    }
    if (products.length) {
      const cid = products[0]?.categoryInfo?.id;
      result.push({
        type: UPSELLS_TYPES.SALSA,
        categoryid: cid != null ? [cid] : [],
        products,
      });
    }
  }

  return result;
}

/**
 * When Remote Config defines `upsells` / `salsas` IDs, matches **chainproductid** on menu products.
 * Options are loaded separately via `/products/{id}/modifiers` using each product’s ordering **id**.
 * Otherwise uses `isUpsell` / `isSalsa` metadata (legacy).
 */
export const getUpsellProductsFromMenu = (
  menu: any,
  remoteConfigData?: unknown,
) => {
  const parsed = parseUpsellsSalsaUtensilsIds(remoteConfigData);
  const useConfigForUpsells =
    parsed &&
    (parsed.upsells.sides.length > 0 ||
      parsed.upsells.desserts.length > 0 ||
      parsed.upsells.drinks.length > 0 ||
      parsed.salsas.length > 0);

  if (useConfigForUpsells) {
    return getUpsellProductsFromConfigIds(menu, parsed!);
  }

  return getUpsellProductsFromMenuMetadata(menu);
};

export const getSalsaProductsFromMenu = (
  menu: any,
  quantityKey = 'quantity',
  remoteConfigData?: unknown,
) => {
  const upsells = getUpsellProductsFromMenu(menu, remoteConfigData);
  const salsaUpsell = upsells.find(
    (u: any) => u.type === UPSELLS_TYPES.SALSA,
  );
  const products = salsaUpsell?.products ?? [];
  return products.map((obj: any) => ({ ...obj, [quantityKey]: 0 }));
};

export const getUpsellsProducts = (upsells: any[]) => {
  const sides = upsells?.filter((item: { type: string }) => item.type === 'SIDE')[0];
  const drinks = upsells?.filter((item: { type: string }) => item.type === 'DRINK')[0];
  const desserts = upsells?.filter((item: { type: string }) => item.type === 'DESSERT')[0];

  return {
    upsellsSides: sides?.products || [],
    upsellsDrinks: drinks?.products || [],
    upsellsDesserts: desserts?.products || [],
  };
};

/**
 * Remaining basket qty per Olo product id (mutated when allocating).
 */
function buildBasketPool(productsArr: any[] | undefined): Record<number, number> {
  const pool: Record<number, number> = {};
  productsArr?.forEach((item: { productId: any; quantity?: number }) => {
    const pid = Number(item.productId);
    if (Number.isNaN(pid)) return;
    const q = Math.max(1, Number(item.quantity ?? 1));
    pool[pid] = (pool[pid] ?? 0) + q;
  });
  return pool;
}

function consumeOneFromUpsellList(
  pool: Record<number, number>,
  list: { id: any }[] | undefined,
): boolean {
  if (!list?.length) return false;
  for (const p of list) {
    const pid = Number(p.id);
    if (Number.isNaN(pid)) continue;
    if ((pool[pid] ?? 0) > 0) {
      pool[pid] -= 1;
      return true;
    }
  }
  return false;
}

/**
 * Per-category “has an upsell from this bucket” flags. Products listed in more than one
 * bucket share basket qty in fixed priority **SIDE → DRINK → DESSERT** so the first unit
 * counts only toward the earlier category (e.g. one Baja Fish Slider sets `sides`, not `desserts`;
 * a second unit can satisfy `desserts`).
 */
export const getUpsellsStatsFromOrder = (productsArr: any[], upsells: any) => {
  const { upsellsSides, upsellsDesserts, upsellsDrinks } = getUpsellsProducts(upsells);
  const pool = buildBasketPool(productsArr);

  const includeSides = consumeOneFromUpsellList(pool, upsellsSides);
  const includeDrinks = consumeOneFromUpsellList(pool, upsellsDrinks);
  const includesDesserts = consumeOneFromUpsellList(pool, upsellsDesserts);

  return {
    sides: includeSides,
    drinks: includeDrinks,
    desserts: includesDesserts,
  };
};

/**
 * Drops upsell carousel rows that the bag already “used up” (same Olo `id` as `basket[].productId`).
 * Dual bucket products (side + dessert) need **2** in the bag when both `sides` and `desserts` are true.
 */
export function filterUpsellCombinationItems(
  items: any[],
  basketProducts: any[] | undefined,
  upsells: any,
  stats: { sides: boolean; desserts: boolean; drinks: boolean },
): any[] {
  if (!items?.length) return items ?? [];
  const pool = buildBasketPool(basketProducts);
  const { upsellsSides, upsellsDesserts, upsellsDrinks } =
    getUpsellsProducts(upsells);

  const inList = (list: any[] | undefined, pid: number) =>
    list?.some((p) => Number(p.id) === pid) ?? false;

  return items.filter((item) => {
    const pid = Number(item?.id);
    if (!item || Number.isNaN(pid)) return false;
    const qty = pool[pid] ?? 0;
    if (qty <= 0) return true;

    const inSide = inList(upsellsSides, pid);
    const inDessert = inList(upsellsDesserts, pid);
    const inDrink = inList(upsellsDrinks, pid);

    if (inSide && inDessert) {
      if (stats.sides && stats.desserts && qty >= 2) return false;
      return true;
    }

    if (inSide && stats.sides && qty >= 1) return false;
    if (inDessert && stats.desserts && qty >= 1) return false;
    if (inDrink && stats.drinks && qty >= 1) return false;

    return true;
  });
}
